import { useRef, useState } from 'react';

import { gruppiereTeile, MUENZEN, SCHEINE, summeTeile } from '../geld';
import { gegebenPlausibel, rueckgeldCent } from '../rueckgeld';
import { formatiereEuro, parseEuro } from '../warenkorb';
import { ZiffernTasten } from './Ziffernblock';
import { BarDialogV2 } from './zahlung-v2/BarDialogV2';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';
import { euroKurz, stueckelungText, zifferEingabe } from './zahlung-v2/logik';
import { zahlungsdialogV2Aktiv } from './zahlung-v2/schalter';

/**
 * Barkassieren mit Rückgeld: Scheine und Münzen antippen (jeder Tipp zählt
 * dazu), „Passend“, Ziffernblock oder freie Eingabe; das Rückgeld erscheint
 * samt Stückelung (POS V2 Phase 2, Rahmen D2 v2). „Kassieren" bestätigt die
 * Zahlung – erst dann entsteht der Beleg (Regel 3).
 */
export function BarDialog(props: {
  summeCent: number;
  laeuft: boolean;
  onKassieren: (gegebenCent: number | null) => void;
  onAbbrechen: () => void;
  /**
   * Fehlerbericht 25.10.2026: Belegnummer eines noch ungeklärten Verkaufs (z. B. nach einem
   * Verbindungsfehler). Dann gibt es kein „Kassieren“ mehr – nur „Beleg prüfen“ für genau diesen
   * Vorgang (gleiche Vorgangs-ID, gleiche Nummer); ein zweiter Verkauf entsteht so nicht.
   */
  ungeklaert?: string | null;
  onBelegPruefen?: () => void;
  /** Kassen-ID – nur für den Schalter der Zahlungsdialoge V2 (Testkasse, siehe schalter.ts). */
  kassenId?: string;
  /** D2 v2: Zusatz im Kopf (z. B. Kunde · Positionen) – nur Anzeige. */
  info?: string;
}) {
  /** Zahlungsdialog V2 (Schalter, Vorgabe AUS) – beim Öffnen gelesen, nur andere Darstellung. */
  const [v2] = useState(() => zahlungsdialogV2Aktiv(props.kassenId));
  /** ZD-V2-F4: Fokusfalle des bisherigen Dialogs (Tab erreicht die Reiter dahinter nicht). */
  const dialog = useRef<HTMLDivElement | null>(null);
  useFokusfalleAmFenster(dialog, !v2);
  /** Angetippte Geldstücke in Cent, in Reihenfolge des Antippens. */
  const [teile, setTeile] = useState<number[]>([]);
  /** Freie Eingabe oder Vorschlag – gilt nur, solange keine Geldstücke angetippt sind. */
  const [manuell, setManuell] = useState<{ text: string; cent: number | null }>({
    text: '',
    cent: null,
  });
  const gegeben = teile.length > 0 ? summeTeile(teile) : manuell.cent;
  const gegebenText = teile.length > 0 ? formatiereEuro(summeTeile(teile)) : manuell.text;
  const rueckgeld = rueckgeldCent(props.summeCent, gegeben);

  function setze(cent: number | null): void {
    setTeile([]);
    setManuell({ text: cent === null ? '' : formatiereEuro(cent), cent });
  }

  function tippe(cent: number): void {
    setTeile((alt) => [...alt, cent]);
  }

  function letztesZurueck(): void {
    setTeile((alt) => alt.slice(0, -1));
  }

  const gruppen = gruppiereTeile(teile);
  /** D2 v2: Münzen auf Abruf („Münzen ▾“). */
  const [muenzenOffen, setMuenzenOffen] = useState(false);

  /** Text im Feld, aber kein lesbarer Betrag (z. B. Scanner-Zeichen) – kassiert nie. */
  const unlesbar = teile.length === 0 && manuell.text.trim() !== '' && manuell.cent === null;
  /** Unplausibel hoher Betrag (z. B. versehentlich gescannter Barcode) kassiert nicht. */
  const plausibel = !unlesbar && (gegeben === null || gegebenPlausibel(props.summeCent, gegeben));
  /** Kassieren nur ohne laufende Anfrage und wenn genug gegeben wurde (oder nichts eingetragen). */
  const kassierbar = !props.laeuft && plausibel && (gegeben === null || rueckgeld !== null);

  if (v2) {
    // Gleiche Werte, gleiche Bedingung `kassierbar`, gleiche Aufrufe wie unten – nur das V2-Layout
    return (
      <BarDialogV2
        summeCent={props.summeCent}
        laeuft={props.laeuft}
        ungeklaert={props.ungeklaert ?? null}
        gegebenCent={gegeben}
        gegebenText={gegebenText}
        teile={teile}
        rueckgeldCent={rueckgeld}
        unlesbar={unlesbar}
        plausibel={plausibel}
        kassierbar={kassierbar}
        onKassieren={() => {
          props.onKassieren(gegeben);
        }}
        onAbbrechen={props.onAbbrechen}
        onBelegPruefen={() => {
          props.onBelegPruefen?.();
        }}
        onSetze={setze}
        onZiffer={(t) => {
          setze(zifferEingabe(gegeben, t));
        }}
        onTippe={tippe}
        onLetztesZurueck={letztesZurueck}
        onEingabe={(text) => {
          setTeile([]);
          setManuell({ text, cent: parseEuro(text) });
        }}
      />
    );
  }

  if (props.ungeklaert != null) {
    return (
      <div
        ref={dialog}
        className="zahlung-dialog"
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ outline: 'none' }}
      >
        <div className="karte bar-dialog">
          <h2>Verkauf noch ungeklärt</h2>
          <p className="warnung" role="alert">
            Verkauf noch ungeklärt. Bitte diesen Einkauf nicht erneut kassieren. Zuerst Belegstatus
            prüfen.
          </p>
          <p>
            Verkauf <strong>{props.ungeklaert}</strong> über {formatiereEuro(props.summeCent)} € –
            Belegnummer und Vorgang bleiben erhalten.
          </p>
          <div className="zeile" style={{ justifyContent: 'center' }}>
            <button
              className="taste gross"
              disabled={props.laeuft}
              onClick={() => {
                props.onBelegPruefen?.();
              }}
            >
              Beleg prüfen
            </button>
            <button className="taste zweit" disabled={props.laeuft} onClick={props.onAbbrechen}>
              Schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // POS V2 Phase 2, Rahmen D2 v2 `29:6423`: links Betrag, Scheine/Münzen addieren, Gegeben und
  // Rückgeld; rechts der Ziffernblock. Gleiche Werte, gleiche Bedingung `kassierbar`, gleiche Aufrufe.
  return (
    <div
      ref={dialog}
      className="zahlung-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bar-titel"
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className="karte dlg bar-dialog bar-zweispaltig">
        <div className="dlg-kopf">
          <h2 id="bar-titel">Bar kassieren</h2>
          {props.info !== undefined && <span className="dlg-meta">{props.info}</span>}
        </div>
        <div className="bar-spalten">
          <div className="bar-links">
            <div className="dlg-betragszeile">
              <span>Zu zahlen</span>
              <span className="betrag">{formatiereEuro(props.summeCent)} €</span>
            </div>
            <p className="hinweis">Scheine und Münzen addieren · „Passend“ setzt den Betrag</p>
            <div className="chips" role="group" aria-label="Scheine">
              <button
                type="button"
                className={gegeben === props.summeCent && teile.length === 0 ? 'aktiv' : ''}
                onClick={() => {
                  setze(props.summeCent);
                }}
              >
                Passend
              </button>
              {SCHEINE.map((g) => (
                <button
                  key={g.cent}
                  type="button"
                  className={teile.includes(g.cent) ? 'aktiv' : ''}
                  title={`${formatiereEuro(g.cent)} € dazu`}
                  onClick={() => {
                    tippe(g.cent);
                  }}
                >
                  {euroKurz(g.cent)}
                </button>
              ))}
              <button
                type="button"
                aria-expanded={muenzenOffen}
                className={muenzenOffen ? 'aktiv' : ''}
                onClick={() => {
                  setMuenzenOffen((o) => !o);
                }}
              >
                Münzen {muenzenOffen ? '▴' : '▾'}
              </button>
            </div>
            {muenzenOffen && (
              <div className="chips" role="group" aria-label="Münzen">
                {MUENZEN.map((g) => (
                  <button
                    key={g.cent}
                    type="button"
                    className={teile.includes(g.cent) ? 'aktiv' : ''}
                    title={`${formatiereEuro(g.cent)} € dazu`}
                    onClick={() => {
                      tippe(g.cent);
                    }}
                  >
                    {euroKurz(g.cent)}
                  </button>
                ))}
              </div>
            )}
            <div className="bar-gezaehlt">
              <span className="hinweis gegeben-teile">
                {teile.length > 0
                  ? `Gezählt: ${gruppen
                      .map((g) => `${String(g.anzahl)} × ${euroKurz(g.wertCent)}`)
                      .join(' · ')}`
                  : 'Gezählt: –'}
              </span>
              <button
                type="button"
                className="taste zweit"
                disabled={teile.length === 0}
                onClick={letztesZurueck}
              >
                Letztes zurück
              </button>
              <button
                type="button"
                className="taste zweit"
                disabled={teile.length === 0 && gegeben === null}
                onClick={() => {
                  setze(null);
                }}
              >
                Löschen
              </button>
            </div>
            <label className="bar-gegeben">
              <span>Gegeben</span>
              <input
                inputMode="decimal"
                value={gegebenText}
                placeholder="leer = passend"
                autoFocus
                onChange={(e) => {
                  setTeile([]);
                  setManuell({ text: e.target.value, cent: parseEuro(e.target.value) });
                }}
                onKeyDown={(e) => {
                  // Gleiche Bedingung wie der Knopf: zu wenig gegeben kassiert nicht.
                  // Gehaltene Taste (Wiederholung) löst nie ein zweites Kassieren aus
                  if (e.key === 'Enter' && !e.repeat && kassierbar) {
                    props.onKassieren(gegeben);
                  }
                }}
              />
            </label>
            {unlesbar ? (
              <p className="warnung">Betrag nicht lesbar – bitte prüfen (Scanner?).</p>
            ) : !plausibel ? (
              <p className="warnung">
                Gegebener Betrag ungewöhnlich hoch – bitte prüfen (Scanner?).
              </p>
            ) : rueckgeld !== null ? (
              <div className="rueckgeld">
                <div className="rueckgeld-band">
                  <span>Rückgeld</span>
                  <strong>{formatiereEuro(rueckgeld)} €</strong>
                </div>
                {rueckgeld > 0 && (
                  <p className="rueckgeld-stuecke" aria-label="Rückgeld-Stückelung">
                    Rückgeld geben: {stueckelungText(rueckgeld)}
                  </p>
                )}
              </div>
            ) : (
              gegeben !== null && <p className="warnung">Gegebener Betrag reicht nicht.</p>
            )}
          </div>
          <ZiffernTasten
            klasse="bar-ziffern"
            beschriftung="Ziffernblock Gegeben"
            fokusHalten
            gesperrt={props.laeuft}
            onTaste={(t) => {
              setze(zifferEingabe(gegeben, t));
            }}
          />
        </div>
        <p className="dlg-sperre hinweis">
          Kasse gesperrt bis zum Abschluss: Kacheln, Suche, Scanner und Funktionstasten reagieren
          nicht.
        </p>
        <div className="dlg-fuss bar-fuss">
          <button
            className="taste zweit"
            data-kuerzel="Esc"
            disabled={props.laeuft}
            onClick={props.onAbbrechen}
          >
            Abbrechen
          </button>
          <button
            className="taste gross bar"
            data-kuerzel="Enter"
            disabled={!kassierbar}
            onClick={() => {
              props.onKassieren(gegeben);
            }}
          >
            {gegeben === null ? 'Kassieren (ohne Rückgeld)' : 'Kassieren'}
          </button>
        </div>
      </div>
    </div>
  );
}
